import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Table,
  Tag,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  PlusOutlined,
  PrinterOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import utils from "utils";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import Loading from "../../../../components/shared-components/Loading";
import { getPaymentStatus } from "../../../../functions/formatters";
import { isMobile } from "../../../../functions/browser";
import {
  FIRESTORE_LEADS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { arrayRemove, deleteField, doc, getDoc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import locale from "antd/es/date-picker/locale/nl_NL";
import {
  DEFAULT_DELIVERY_LABEL_FALLBACK,
  FRESHING_BOX_DELIVERY_LABELS,
  ORDER_STATUS_CANCELED,
  ORDER_STATUS_CREATED,
  ORDER_STATUS_DELIVERED,
  ORDER_STATUS_DRAFT,
  ORDER_STATUS_PACKED,
  ORDER_STATUS_SHIPPED,
  SOO_COOL_DELIVERY_LABELS,
} from "../../../../constants/OrderConstant";
import { getFirebaseIDToken } from "../../../../functions/firebase/firebase";
import styled from "styled-components";
import { shippingLabelsCombinedApi, sooCoolOrder, stockApiUrl } from "../../../../urls";
import axios from "axios";
import { generateMultiplePicklists, generatePicklist } from "../../../../functions/print";
import AddShipmentModal from "./modals/AddShipmentModal";
import ConfirmDraftShipmentModal from "./modals/ConfirmDraftShipmentModal";
import EditCratesModal from "./modals/EditCratesModal";
import BulkSendToFreshingBoxModal from "./modals/BulkSendToFreshingBoxModal";
import dayjs from "dayjs";
import { useOrders } from "../../../../data/hooks/firestore/useOrders";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { useLeads } from "../../../../data/hooks/firestore/useLeads";
import { LEAD_STATUS_NEW } from "../../../../constants/LeadConstant";
import { handleOrderPackingStatusChange } from "../../../../functions/orders";
import roles from "../../../../roles";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import useIsMobile from "../../../../hooks/useIsMobile";
import Popover from "antd/es/popover";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import { sendOrderToFreshingBox } from "../../../../functions/api/freshingbox";

const { RangePicker } = DatePicker;

const Shipments = () => {
  const { t } = useTranslation();
  const { profile, email } = useAuth();
  const [maxDaysAgo, setMaxDaysAgo] = useState(30);
  const [customDateRange, setCustomDateRange] = useState(null);
  const { data: orders, loading: ordersLoading, error: ordersError } = useOrders(maxDaysAgo, customDateRange);
  const { leads, isLoading: leadsLoading } = useLeads();
  const { products, isLoading: productsLoading } = useProducts();
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [visible, setVisible] = useState(false);
  const [order, setOrder] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [datePickerForm] = Form.useForm();
  const [dateRange, setDateRange] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [addShipmentModalVisible, setAddShipmentModalVisible] = useState(false);
  const [confirmDraftModalVisible, setConfirmDraftModalVisible] = useState(false);
  const [selectedDraftOrder, setSelectedDraftOrder] = useState(null);
  const [dateSelectionModalVisible, setDateSelectionModalVisible] = useState(false);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const [freshingBoxLoadingOrders, setFreshingBoxLoadingOrders] = useState(new Set());
  const [sooCoolLoadingOrders, setSooCoolLoadingOrders] = useState(new Set());
  const [editCratesModalVisible, setEditCratesModalVisible] = useState(false);
  const [selectedOrderForCrates, setSelectedOrderForCrates] = useState(null);
  const [selectedOrdersForBulkCrates, setSelectedOrdersForBulkCrates] = useState(null);
  const [isFetchingShippingLabels, setIsFetchingShippingLabels] = useState(false);
  const [bulkFreshingBoxModalVisible, setBulkFreshingBoxModalVisible] = useState(false);
  const [selectedOrdersForBulkFreshingBox, setSelectedOrdersForBulkFreshingBox] = useState([]);
  const isMobileView = useIsMobile();

  // Status filter options
  const statusOptions = [
    { value: "all", label: t("all_statuses") },
    { value: ORDER_STATUS_CREATED, label: getPaymentStatus(ORDER_STATUS_CREATED) },
    { value: ORDER_STATUS_DRAFT, label: getPaymentStatus(ORDER_STATUS_DRAFT) },
    { value: ORDER_STATUS_PACKED, label: getPaymentStatus(ORDER_STATUS_PACKED) },
    { value: ORDER_STATUS_SHIPPED, label: getPaymentStatus(ORDER_STATUS_SHIPPED) },
    { value: ORDER_STATUS_DELIVERED, label: getPaymentStatus(ORDER_STATUS_DELIVERED) },
    { value: ORDER_STATUS_CANCELED, label: getPaymentStatus(ORDER_STATUS_CANCELED) },
  ];

  useEffect(() => {
    if (ordersError) {
      console.error("Error loading orders:", ordersError);
      message.error(t("notifications.error_loading_orders"));
      return;
    }

    if (orders) {
      // Filter ONLY acquisition orders (type === "acquisition")
      let filteredByType = orders.filter((order) => order.type === "acquisition");

      // Apply status filter
      if (selectedStatus && selectedStatus !== "all") {
        filteredByType = filteredByType.filter((order) => order.farmorderStatus === selectedStatus);
      }

      // Apply date filter if present
      let dateFiltered = filteredByType;
      if (dateRange && dateRange.length > 0) {
        dateFiltered = utils.filterOrdersDateRange(filteredByType, dateRange);
      }

      // Apply search filter if present
      let finalFiltered = dateFiltered;
      if (searchText) {
        finalFiltered = utils.wildCardSearchOnOrder(dateFiltered, searchText);
      }

      setFilteredOrders(finalFiltered);
    }
  }, [orders, ordersError, dateRange, searchText, selectedStatus]);

  useEffect(() => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
  }, []);

  const handleDateSelection = (date) => {
    setSelectedDeliveryDate(date);
    setDateSelectionModalVisible(false);
    setAddShipmentModalVisible(true);
  };

  const handlePeriodChange = (period) => {
    setMaxDaysAgo(period);
    setCustomDateRange(null);
    setSelectedRows([]);
    setSelectedRowKeys([]);
    setDateRange([]);
    datePickerForm.resetFields();
  };

  const handleCustomDateRange = (range) => {
    if (range && range[0] && range[1]) {
      setCustomDateRange([range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")]);
      setMaxDaysAgo(null);
    } else {
      setCustomDateRange(null);
      setMaxDaysAgo(30);
    }
    setSelectedRows([]);
    setSelectedRowKeys([]);
  };

  const showDrawer = (row) => {
    setVisible(true);
    setOrder(row);
  };

  const closeDrawer = () => {
    setVisible(false);
    setTimeout(() => {
      setOrder(null);
    }, 400);
  };

  const openConfirmDraftModal = (order) => {
    setSelectedDraftOrder(order);
    setConfirmDraftModalVisible(true);
  };

  const closeConfirmDraftModal = () => {
    setConfirmDraftModalVisible(false);
    setSelectedDraftOrder(null);
  };

  const cancelOrderInSooCool = async (sooCoolId, orderId) => {
    if (!sooCoolId) return;
    try {
      const token = await getFirebaseIDToken();
      await axios
        .delete(sooCoolOrder + `/${sooCoolId}`, {
          headers: { Authorization: token },
        })
        .catch((err) => {
          throw new Error(err.response.data.message);
        });

      await updateDoc(doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, orderId), {
        oldSooCoolId: sooCoolId,
        sooCoolId: null,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success(t("notifications.order_cancelled_soo_cool"));
    } catch (error) {
      console.error("Error canceling order:", error);
      message.error(t("notifications.error_canceling_order_soo_cool"));
    }
  };

  const deleteOrder = async (row) => {
    try {
      // Handle additional orders logic if applicable
      const isMainOrder =
        row.additionalOrderIds && Array.isArray(row.additionalOrderIds) && row.additionalOrderIds.length > 0;

      if (isMainOrder) {
        const additionalOrders = orders
          .filter((o) => row.additionalOrderIds.includes(o.id))
          .sort((a, b) => a.orderDateTime - b.orderDateTime);

        if (additionalOrders.length > 0) {
          const newMainOrder = additionalOrders[0];
          const remainingAdditional = additionalOrders.slice(1);

          await updateDoc(
            doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, newMainOrder.id),
            {
              mainOrderId: deleteField(),
              deliveryLabel: DEFAULT_DELIVERY_LABEL_FALLBACK,
              additionalOrderIds: remainingAdditional.map((o) => o.id),
              updatedAt: dayjs().unix(),
              updatedBy: email,
            },
          );

          for (const order of remainingAdditional) {
            await updateDoc(
              doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, order.id),
              { mainOrderId: newMainOrder.id, updatedAt: dayjs().unix(), updatedBy: email },
            );
          }
        }
      }

      const isAdditionalOrder = !!row.mainOrderId;
      if (isAdditionalOrder) {
        await updateDoc(
          doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, row.mainOrderId),
          {
            additionalOrderIds: arrayRemove(row.id),
            updatedAt: dayjs().unix(),
            updatedBy: email,
          },
        );
      }

      // Cancel in SooCool if applicable
      if (row.sooCoolId) {
        await cancelOrderInSooCool(row.sooCoolId, row.id);
      }

      // Update order status to canceled
      await updateDoc(doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, row.id), {
        farmorderStatus: ORDER_STATUS_CANCELED,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      // UPDATE LEAD STATUS BACK TO NEW
      if (row.leadId) {
        try {
          await updateDoc(
            doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, row.leadId),
            { status: LEAD_STATUS_NEW, shipmentCreated: false },
          );
        } catch (leadError) {
          console.error("Error updating lead status:", leadError);
          // Don't fail the entire operation if lead update fails
        }
      }

      // Update stock (only for non-draft orders)
      if (row.farmorderStatus !== ORDER_STATUS_DRAFT) {
        const updatedOrderRef = await getDoc(
          doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, row.id),
        );
        const updatedOrder = updatedOrderRef.data();

        const token = await getFirebaseIDToken();
        await axios.put(stockApiUrl, updatedOrder, {
          headers: { Authorization: token },
        });
      }

      message.success(t("notifications.shipment_deleted"));
    } catch (error) {
      console.error("Error deleting shipment:", error);
      message.error(t("notifications.error_deleting_shipment"));
    }
  };

  const handleSendToFreshingBox = async (order) => {
    // Validate scannedCrates
    if (!order.scannedCrates || order.scannedCrates.length === 0) {
      message.warning(t("notifications.freshingbox_requires_scanned_crates"));
      return;
    }

    setFreshingBoxLoadingOrders((prev) => new Set(prev).add(order.id));

    try {
      const token = await getFirebaseIDToken();
      if (!token) {
        message.error(t("notifications.authentication_error"));
        return;
      }

      const result = await sendOrderToFreshingBox(order, token);

      if (result.success) {
        message.success(t("notifications.order_sent_to_freshingbox"));
      } else {
        message.error(t("notifications.error_sending_to_freshingbox") + ": " + result.error);
      }
    } catch (error) {
      console.error("FreshingBox error:", error);
      message.error(t("notifications.error_sending_to_freshingbox") + ": " + error.message);
    } finally {
      setFreshingBoxLoadingOrders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(order.id);
        return newSet;
      });
    }
  };

  const handleSendToSooCool = async (order, type = "create") => {
    setSooCoolLoadingOrders((prev) => new Set(prev).add(order.id));

    try {
      const token = await getFirebaseIDToken();
      if (!token) {
        message.error(t("notifications.error_getting_id_token"));
        return;
      }

      const config = {
        headers: { Authorization: token },
      };

      if (type === "create") {
        const response = await axios.post(sooCoolOrder, order, config);
        message.success(t("notifications.order_saved_to_soo_cool"));
        return response;
      } else if (type === "update") {
        const response = await axios.put(sooCoolOrder + "/" + order.sooCoolId, order, config);
        message.success(t("notifications.order_updated_in_soo_cool"));
        return response;
      }
    } catch (error) {
      console.error("SooCool error:", error);
      message.error(t("notifications.error_update_order_to_soo_cool"));
    } finally {
      setSooCoolLoadingOrders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(order.id);
        return newSet;
      });
    }
  };

  const handlePackedStatusChange = async (orderId, packed) => {
    try {
      const order = orders.find((o) => o.id === orderId);
      if (!order) {
        message.error(t("notifications.order_not_found"));
        return;
      }

      // Use existing function but skip invoice creation for acquisition orders
      const result = await handleOrderPackingStatusChange(order, packed);
      if (!result.success) {
        message.error(t("notifications.error_updating_packed_status"));
        return;
      }

      // Don't include invoiceId in update for acquisition orders
      const updateObject = { ...result.updateObject };
      delete updateObject.invoiceId;

      await updateDoc(doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, orderId), {
        ...updateObject,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success(packed ? t("notifications.shipment_packed") : t("notifications.shipment_unpacked"));

      setFilteredOrders((prev) =>
        prev
          .map((order) =>
            order.id === orderId
              ? {
                  ...order,
                  packed,
                  farmorderStatus: packed ? ORDER_STATUS_PACKED : ORDER_STATUS_CREATED,
                  farmorderId: result.updateObject.farmorderId || order.farmorderId,
                }
              : order,
          )
          .sort((a, b) => {
            const aPacked = a.packed || false;
            const bPacked = b.packed || false;
            return aPacked === bPacked ? 0 : aPacked ? 1 : -1;
          }),
      );
    } catch (error) {
      console.error("Error updating packed status:", error);
      message.error(t("notifications.error_updating_packed_status"));
    }
  };

  const handleBulkPackedStatusChange = async (packed) => {
    if (selectedRows.length === 0) {
      message.error(t("notifications.no_orders_selected"));
      return;
    }

    const eligibleOrders = selectedRows.filter(
      (o) => o.farmorderStatus !== ORDER_STATUS_CANCELED && o.farmorderStatus !== ORDER_STATUS_DRAFT,
    );

    if (eligibleOrders.length === 0) {
      message.warning(t("notifications.no_printable_orders_selected"));
      return;
    }

    const loadingKey = "bulk-packing-shipments";
    message.loading({ content: t("notifications.generating_documents"), key: loadingKey, duration: 0 });

    let successCount = 0;
    let failedCount = 0;
    const updatedById = {};

    for (const order of eligibleOrders) {
      try {
        const result = await handleOrderPackingStatusChange(order, packed);
        if (!result.success) {
          failedCount++;
          continue;
        }

        const updateObject = { ...result.updateObject };
        delete updateObject.invoiceId;

        await updateDoc(
          doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, order.id),
          {
            ...updateObject,
            updatedAt: dayjs().unix(),
            updatedBy: email,
          },
        );

        updatedById[order.id] = {
          packed,
          farmorderStatus: packed ? ORDER_STATUS_PACKED : ORDER_STATUS_CREATED,
          farmorderId: result.updateObject.farmorderId || order.farmorderId,
        };
        successCount++;
      } catch (error) {
        console.error("Error updating packed status for order", order.id, error);
        failedCount++;
      }
    }

    setFilteredOrders((prev) =>
      prev
        .map((o) => (updatedById[o.id] ? { ...o, ...updatedById[o.id] } : o))
        .sort((a, b) => {
          const aPacked = a.packed || false;
          const bPacked = b.packed || false;
          return aPacked === bPacked ? 0 : aPacked ? 1 : -1;
        }),
    );

    if (failedCount === 0) {
      message.success({
        content: t(packed ? "notifications.bulk_shipments_packed" : "notifications.bulk_shipments_unpacked", {
          count: successCount,
        }),
        key: loadingKey,
        duration: 2,
      });
    } else {
      message.warning({
        content: t(
          packed ? "notifications.bulk_shipments_packed_partial" : "notifications.bulk_shipments_unpacked_partial",
          {
            success: successCount,
            failed: failedCount,
            total: eligibleOrders.length,
          },
        ),
        key: loadingKey,
        duration: 3,
      });
    }

    setSelectedRows([]);
    setSelectedRowKeys([]);
  };

  const confirmBulkPackedStatusChange = (packed) => {
    if (selectedRows.length === 0) {
      message.error(t("notifications.no_orders_selected"));
      return;
    }

    Modal.confirm({
      title: t(packed ? "confirm_bulk_pack_shipments" : "confirm_bulk_unpack_shipments", {
        count: selectedRows.length,
      }),
      okText: t("yes"),
      cancelText: t("no"),
      onOk: () => handleBulkPackedStatusChange(packed),
    });
  };

  const dropdownMenu = (row) => (
    <Menu>
      {row.farmorderStatus === ORDER_STATUS_DRAFT && (
        <Menu.Item onClick={() => openConfirmDraftModal(row)}>
          <Flex alignItems="center">
            <CheckOutlined />
            <span style={{ marginLeft: 4 }}>{t("confirm_shipment")}</span>
          </Flex>
        </Menu.Item>
      )}
      <Menu.Item onClick={() => showDrawer(row)}>
        <Flex alignItems="center">
          <EyeOutlined />
          <span style={{ marginLeft: 4 }}>{t("details")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item onClick={() => openEditCratesModal(row)}>
        <Flex alignItems="center">
          <EditOutlined />
          <span style={{ marginLeft: 4 }}>{t("edit_crates")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item onClick={() => openDuplicateModal(row)}>
        <Flex alignItems="center">
          <CopyOutlined />
          <span style={{ marginLeft: 4 }}>{t("duplicate")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item disabled={row.packed}>
        <Popconfirm
          placement="bottom"
          title={t("confirm_delete_shipment", { name: row.farmorderId })}
          onConfirm={() => deleteOrder(row)}
          okText={t("yes")}
          cancelText={t("no")}
        >
          <a onClick={(e) => e.preventDefault}>
            <DeleteOutlined />
            <span style={{ marginLeft: 4 }}>{t("cancel")}</span>
          </a>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const printMenu = (
    <Menu>
      <Menu.Item key="1">
        <a
          onClick={() => {
            const filteredOrders = selectedRows.length > 0 ? selectedRows : [];
            if (filteredOrders.length === 0) {
              message.error(t("notifications.no_orders_selected"));
              return;
            }

            const orderIds = filteredOrders.map((o) => o.id);
            fetchAndOpenShippingLabelsPdf({ orderIds, download: false });
          }}
        >
          {t("shipping_labels")}
        </a>
      </Menu.Item>
      <Menu.Item key="3">
        <a
          onClick={() => {
            const filteredOrders = selectedRows.length > 0 ? selectedRows : [];
            if (filteredOrders.length === 0) {
              message.error(t("notifications.no_orders_selected"));
              return;
            }

            const orderIds = filteredOrders.map((o) => o.id);
            fetchAndOpenShippingLabelsPdf({ orderIds, download: true });
          }}
        >
          {t("download_shipping_labels")}
        </a>
      </Menu.Item>
      <Menu.Item key="2">
        <a
          onClick={() => {
            const filteredOrders = selectedRows.length > 0 ? selectedRows : [];
            if (filteredOrders.length === 0) {
              message.error(t("notifications.no_orders_selected"));
              return;
            }

            if (filteredOrders.length === 1) {
              generatePicklist(filteredOrders[0], leads, "print");
            } else {
              generateMultiplePicklists(filteredOrders, leads, "print");
            }
          }}
        >
          {t("picking_slips")}
        </a>
      </Menu.Item>
    </Menu>
  );

  const getOrderStatusColor = (status) => {
    if (status === ORDER_STATUS_CREATED) return "yellow";
    else if (status === ORDER_STATUS_PACKED) return "lime";
    else if (status === ORDER_STATUS_CANCELED) return "red";
    else if (status === ORDER_STATUS_DRAFT) return "blue";
    else return "green";
  };

  const tableColumns = [
    {
      title:
        selectedRowKeys.length > 0 ? (
          <span
            style={{ color: "#880e4f", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRowKeys([]);
              setSelectedRows([]);
            }}
            title="Clear selection"
          >
            ({selectedRowKeys.length})
          </span>
        ) : (
          ""
        ),
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
    {
      title: t("order_date"),
      dataIndex: "farmorderDateTime",
      sorter: {
        compare: (a, b) => utils.antdTableSorter(a, b, "farmorderDateTime"),
        multiple: 1,
      },
      render: (_, data) => (
        <button className={"text-button"} onClick={() => showDrawer(data)}>
          <span style={{ minWidth: "120px", display: "block" }}>
            {dayjs(data.farmorderDateTime, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY HH:mm")}
          </span>
        </button>
      ),
      defaultSortOrder: "ascend",
    },
    {
      title: t("company"),
      sorter: (a, b) => utils.antdTableSorter(a, b, "farmorderCustomerName"),
      render: (_, data) => (
        <div style={{ display: "flex", gap: 6 }}>
          {data.farmorderCustomerName}{" "}
          {!!data.farmorderUserFirstName
            ? `(${data.farmorderUserFirstName} ${data.farmorderUserLastName})`
            : `(${data.farmorderUserId})`}
        </div>
      ),
    },
    {
      title: t("delivery_date"),
      dataIndex: "farmorderDeliveryDate",
      render: (data) => (
        <span style={{ minWidth: "110px", display: "block" }}>
          {dayjs(data, "YYYY-MM-DD").format("ddd D MMM YYYY")}
        </span>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "farmorderDeliveryDate"),
    },
    {
      title: t("status"),
      dataIndex: "farmorderStatus",
      render: (data) => (
        <span>
          <Tag color={getOrderStatusColor(data)}>{getPaymentStatus(data)}</Tag>
        </span>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "farmorderStatus"),
    },
    {
      title: t("delivery_label"),
      render: (_, order) => {
        const deliveryLabel = order.deliveryLabel;

        if (SOO_COOL_DELIVERY_LABELS.includes(deliveryLabel)) {
          if (order.farmorderStatus === ORDER_STATUS_CANCELED) {
            return <span>{deliveryLabel}</span>;
          }

          if (order.sooCoolId) {
            return (
              <span>
                {deliveryLabel} - {order.sooCoolOrderKey || order.sooCoolId}
              </span>
            );
          } else {
            const isLoading = sooCoolLoadingOrders.has(order.id);

            if (isLoading) {
              return (
                <span>
                  {deliveryLabel} <SyncOutlined spin />
                </span>
              );
            }

            return (
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item>
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          handleSendToSooCool(order, "create");
                        }}
                      >
                        {t("send_to_soo_cool")}
                      </a>
                    </Menu.Item>
                  </Menu>
                }
                trigger={["click"]}
              >
                <a onClick={(e) => e.preventDefault()}>
                  <span>
                    {deliveryLabel} ({t("no")}) <DownOutlined />
                  </span>
                </a>
              </Dropdown>
            );
          }
        } else if (FRESHING_BOX_DELIVERY_LABELS.includes(deliveryLabel)) {
          if (order.farmorderStatus === ORDER_STATUS_CANCELED) {
            return <span>{order.deliveryLabel}</span>;
          }

          if (order.freshingBoxTrackingNumber) {
            return <span>{order.deliveryLabel}</span>;
          } else {
            const isLoading = freshingBoxLoadingOrders.has(order.id);

            if (isLoading) {
              return (
                <span>
                  {order.deliveryLabel} <SyncOutlined spin />
                </span>
              );
            }

            return (
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item>
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          handleSendToFreshingBox(order);
                        }}
                      >
                        {t("send_to_freshingbox")}
                      </a>
                    </Menu.Item>
                  </Menu>
                }
              >
                <a onClick={(e) => e.preventDefault()}>
                  {order.deliveryLabel} <DownOutlined />
                </a>
              </Dropdown>
            );
          }
        } else {
          return <span>{order.deliveryLabel}</span>;
        }
      },
    },
    {
      title: t("packed"),
      dataIndex: "packed",
      sorter: {
        compare: (a, b) => {
          const aPacked = a.packed || false;
          const bPacked = b.packed || false;
          return aPacked === bPacked ? 0 : aPacked ? 1 : -1;
        },
        multiple: 2,
      },
      render: (_, order) => (
        <Checkbox
          checked={order.packed || false}
          disabled={order.farmorderStatus === ORDER_STATUS_CANCELED || order.farmorderStatus === ORDER_STATUS_DRAFT}
          onChange={(e) => handlePackedStatusChange(order.id, e.target.checked)}
        />
      ),
    },
    {
      title: "",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const onSearch = (value) => {
    setSearchText(value);
  };

  const rowSelection = {
    onChange: (key, rows) => {
      setSelectedRows(rows);
      setSelectedRowKeys(key);
    },
  };

  const handleDateRange = (dateRange) => {
    setDateRange(dateRange);
    setSelectedRows([]);
    setSelectedRowKeys([]);
  };

  const openAddModal = () => {
    setDateSelectionModalVisible(true);
  };

  const closeAddModal = () => {
    setAddShipmentModalVisible(false);
    setSelectedDeliveryDate(null);
    setOrder(null);
  };

  const openDuplicateModal = (order) => {
    setOrder(order);
    setSelectedDeliveryDate(null);
    setAddShipmentModalVisible(true);
  };

  const openEditCratesModal = (order) => {
    setSelectedOrderForCrates(order);
    setSelectedOrdersForBulkCrates(null);
    setEditCratesModalVisible(true);
  };

  const openBulkEditCratesModal = () => {
    if (selectedRows.length === 0) {
      message.error(t("notifications.no_orders_selected"));
      return;
    }
    setSelectedOrderForCrates(null);
    setSelectedOrdersForBulkCrates(selectedRows);
    setEditCratesModalVisible(true);
  };

  const closeEditCratesModal = () => {
    setEditCratesModalVisible(false);
    setSelectedOrderForCrates(null);
    setSelectedOrdersForBulkCrates(null);
  };

  const openBulkFreshingBoxModal = () => {
    if (selectedRows.length === 0) {
      message.error(t("notifications.no_orders_selected"));
      return;
    }
    setSelectedOrdersForBulkFreshingBox(selectedRows);
    setBulkFreshingBoxModalVisible(true);
  };

  const closeBulkFreshingBoxModal = ({ anySuccess } = {}) => {
    setBulkFreshingBoxModalVisible(false);
    setSelectedOrdersForBulkFreshingBox([]);
    if (anySuccess) {
      setSelectedRows([]);
      setSelectedRowKeys([]);
    }
  };

  const bulkActionsMenu = (
    <Menu>
      <Menu.Item key="bulk-edit-crates" onClick={openBulkEditCratesModal}>
        <Flex alignItems="center">
          <EditOutlined />
          <span style={{ marginLeft: 4 }}>{t("edit_crates")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item key="bulk-create-shipments" onClick={openBulkFreshingBoxModal}>
        <Flex alignItems="center">
          <SyncOutlined />
          <span style={{ marginLeft: 4 }}>{t("create_shipments")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item key="bulk-pack-shipments" onClick={() => confirmBulkPackedStatusChange(true)}>
        <Flex alignItems="center">
          <CheckOutlined />
          <span style={{ marginLeft: 4 }}>{t("bulk_pack_shipments")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item key="bulk-unpack-shipments" onClick={() => confirmBulkPackedStatusChange(false)}>
        <Flex alignItems="center">
          <CloseOutlined />
          <span style={{ marginLeft: 4 }}>{t("bulk_unpack_shipments")}</span>
        </Flex>
      </Menu.Item>
    </Menu>
  );

  const fetchAndOpenShippingLabelsPdf = async ({ orderIds, download = false }) => {
    if (!orderIds || orderIds.length === 0) {
      message.error(t("notifications.no_orders_selected"));
      return;
    }

    if (isFetchingShippingLabels) {
      return;
    }

    const selectedOrders = (orders || []).filter((o) => orderIds.includes(o.id));
    const canceledOrders = selectedOrders.filter((o) => o?.farmorderStatus === ORDER_STATUS_CANCELED);

    const eligibleOrderIds = selectedOrders
      .filter((o) => o?.farmorderStatus !== ORDER_STATUS_CANCELED)
      .map((o) => o.id);

    if (canceledOrders.length > 0) {
      if (eligibleOrderIds.length === 0) {
        message.warning(t("notifications.no_printable_orders_selected"));
        return;
      }

      message.warning(`Shipping labels cannot be generated for canceled orders. These will be skipped.`);
    }

    const loadingKey = "fetching-shipping-labels";

    try {
      setIsFetchingShippingLabels(true);
      message.loading({ content: t("notifications.generating_documents"), key: loadingKey, duration: 0 });

      const token = await getFirebaseIDToken();

      // Handle regular, SooCool and FreshingBox shipping labels (all combined server-side into a single PDF)
      if (eligibleOrderIds.length === 0) {
        message.error({ content: t("notifications.no_orders_selected"), key: loadingKey });
        setIsFetchingShippingLabels(false);
        return;
      }

      const url = `${shippingLabelsCombinedApi}${download ? "?download=1" : ""}`;

      const response = await axios.post(
        url,
        { orderIds: eligibleOrderIds },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);

      if (download) {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `shipping-labels-${dayjs().format("DD-MM-YYYY-HH-mm")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const printWindow = window.open(blobUrl, "_blank");
        if (!printWindow) {
          message.warning(t("notifications.please_allow_pop_ups"));
        }
      }

      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(blobUrl);
        } catch (e) {
          // ignore
        }
      }, 10_000);

      message.success({
        content: t("notifications.documents_success", {
          method: download ? t("notifications.downloaded") : t("notifications.opened_for_printing"),
        }),
        key: loadingKey,
        duration: 2,
      });
      setIsFetchingShippingLabels(false);
    } catch (error) {
      console.error("Error fetching shipping labels:", error);
      message.error({ content: t("notifications.error_generating_documents"), key: loadingKey });
      setIsFetchingShippingLabels(false);
    }
  };

  if (ordersLoading || leadsLoading || productsLoading || !orders || !leads || !products) {
    return <Loading />;
  }

  return (
    <>
      <Row className={"mb-2"}>
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>{t("shipments")}</h1>
        </Col>
        <Col xs={24}>
          <Card>
            <Row gutter={[16, 8]} style={{ justifyContent: "space-between", marginBottom: ".5rem" }}>
              {!isMobileView ? (
                <FiltersWrapper>
                  <Input
                    placeholder={t("search")}
                    style={{ width: 200, maxHeight: 40 }}
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => onSearch(e.target.value)}
                    allowClear
                  />
                  <Select
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    style={{ width: 150 }}
                    placeholder={t("filter_by_status")}
                  >
                    {statusOptions.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </FiltersWrapper>
              ) : (
                <FiltersWrapper>
                  <Popover
                    content={
                      <FilterPopoverContent>
                        <div>
                          <FilterLabel>{t("search")}</FilterLabel>
                          <Input
                            placeholder={t("search")}
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => onSearch(e.target.value)}
                            allowClear
                            size="large"
                          />
                        </div>
                        <div>
                          <FilterLabel>{t("filter_by_status")}</FilterLabel>
                          <Select
                            value={selectedStatus}
                            onChange={setSelectedStatus}
                            style={{ width: "100%" }}
                            placeholder={t("filter_by_status")}
                            size="large"
                          >
                            {statusOptions.map((option) => (
                              <Select.Option key={option.value} value={option.value}>
                                {option.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                        <div style={{ marginTop: "1rem" }}>
                          <FilterLabel>{t("filter_on_delivery_date")}</FilterLabel>
                          <Form form={datePickerForm}>
                            <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
                              <RangePicker
                                onChange={(e) => handleDateRange(e)}
                                locale={locale}
                                placeholder={[t("start"), t("end")]}
                                format={"DD-MM-YYYY"}
                                style={{ width: "100%" }}
                                size="large"
                                popupClassName="single-month-range-picker"
                              />
                            </Form.Item>
                          </Form>
                        </div>
                        <FilterButtonsContainer>
                          <Button
                            onClick={() => {
                              setSearchText("");
                              setSelectedStatus("all");
                              datePickerForm.resetFields();
                              handleDateRange(null);
                              setFilterPopoverVisible(false);
                            }}
                            style={{ flex: 1 }}
                          >
                            {t("reset")}
                          </Button>
                          <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                            {t("apply")}
                          </Button>
                        </FilterButtonsContainer>
                      </FilterPopoverContent>
                    }
                    title={t("filters")}
                    trigger="click"
                    open={filterPopoverVisible}
                    onOpenChange={setFilterPopoverVisible}
                    placement="bottomLeft"
                  >
                    <Button
                      icon={<FilterOutlined />}
                      style={{
                        borderColor: searchText || selectedStatus !== "all" || dateRange?.length > 0 ? "#FF4B33" : "",
                        color: searchText || selectedStatus !== "all" || dateRange?.length > 0 ? "#FF4B33" : "",
                      }}
                    />
                  </Popover>
                </FiltersWrapper>
              )}
              <ButtonsWrapper>
                {!isMobileView && (
                  <Form form={datePickerForm}>
                    <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
                      <RangePicker
                        onChange={(e) => handleDateRange(e)}
                        locale={locale}
                        placeholder={[t("start"), t("end")]}
                        format={"DD-MM-YYYY"}
                        style={{ width: "145px" }}
                      />
                    </Form.Item>
                  </Form>
                )}
                {selectedRows.length > 0 && (
                  <Dropdown overlay={bulkActionsMenu} trigger={["click"]}>
                    <Button>
                      {t("bulk_actions")} ({selectedRows.length}) <DownOutlined />
                    </Button>
                  </Dropdown>
                )}
                <Dropdown overlay={printMenu}>
                  <Button className="ant-btn-primary">
                    <PrinterOutlined />
                    {!isMobile && t("print")} <DownOutlined />
                  </Button>
                </Dropdown>
                <Button type="primary" onClick={openAddModal}>
                  <PlusOutlined /> {!isMobile && t("create_shipment")}
                </Button>
              </ButtonsWrapper>
            </Row>
            <div className="table-responsive">
              <Table
                showSorterTooltip={false}
                columns={tableColumns}
                dataSource={filteredOrders}
                rowKey={(record) => record.id || record.farmorderId}
                scroll={{ x: 1300 }}
                rowSelection={{
                  selectedRowKeys: selectedRowKeys,
                  type: "checkbox",
                  preserveSelectedRowKeys: false,
                  ...rowSelection,
                }}
                onRow={(record) => ({
                  style: record.packed ? { backgroundColor: "#fafafa" } : {},
                })}
                pagination={{ showSizeChanger: true }}
              />
            </div>
            {!!order && (
              <Drawer
                title={
                  order.farmorderCustomerName +
                  " | " +
                  dayjs(order.farmorderDateTime, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY HH:mm")
                }
                placement="right"
                onClose={closeDrawer}
                open={visible}
                width={isMobile ? window.innerWidth : 850}
                styles={{ body: { padding: isMobile ? 2 : " 16px 16px 50px 16px" } }}
              >
                <Descriptions
                  bordered
                  title={
                    <div className="flex-row">
                      {t("shipment")}{" "}
                      <Tag color={getOrderStatusColor(order.farmorderStatus)}>
                        {getPaymentStatus(order.farmorderStatus)}
                      </Tag>
                    </div>
                  }
                  size="small"
                  column={1}
                >
                  <Descriptions.Item label={t("id")}>
                    {profile?.userRole === roles.superAdmin ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const win = window.open("", "_blank");
                          win.document.write(`<pre>${JSON.stringify(order, null, 2)}</pre>`);
                          win.document.close();
                        }}
                        style={{ color: "rgba(0, 0, 0, 0.88)", cursor: "default" }}
                      >
                        {order.id}
                      </a>
                    ) : (
                      order.id
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("order_id")}>{order.farmorderId}</Descriptions.Item>
                  <Descriptions.Item label={t("order_date")}>
                    {dayjs(order.farmorderDateTime, "YYYY-MM-DD HH:mm:ss").format("D MMM YYYY HH:mm")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("company")}>{order.farmorderCustomerName}</Descriptions.Item>
                  <Descriptions.Item label={t("order.orderer")}>
                    {!!order.farmorderUserFirstName
                      ? order.farmorderUserFirstName + " " + order.farmorderUserLastName
                      : order.farmorderUserId}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("delivery_date")}>
                    {dayjs(order.farmorderDeliveryDate, "YYYY-MM-DD").format("ddd D MMM YYYY")}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("delivery_label")}>{order.deliveryLabel}</Descriptions.Item>
                  <Descriptions.Item label={t("delivery_time_slot")}>
                    {order.deliveryTimeSlot
                      ? `${order.deliveryTimeSlot.start} - ${order.deliveryTimeSlot.end}${
                          order.deliveryTimeSlot.deliverySurcharge > 0
                            ? ` (+${CURRENCY}${order.deliveryTimeSlot.deliverySurcharge})`
                            : ""
                        }`
                      : "-"}
                  </Descriptions.Item>
                  {!!order.sooCoolId && (
                    <Descriptions.Item label={t("soo_cool_id")}>{order.sooCoolId}</Descriptions.Item>
                  )}
                  {!!order.trackAndTraceLink && (
                    <Descriptions.Item label={t("soocool_track_and_trace")}>
                      <a href={order.trackAndTraceLink} target={"_blank"}>
                        {order.trackAndTraceLink}
                      </a>
                    </Descriptions.Item>
                  )}
                  {!!order.freshingBoxTrackingNumber && (
                    <Descriptions.Item label={t("freshing_box_tracking_number")}>
                      {order.freshingBoxTrackingNumber}
                    </Descriptions.Item>
                  )}
                </Descriptions>
                {!!order.userShoppingCart && (
                  <Descriptions
                    bordered
                    title={<div className="flex-row mt-4">{t("products")}</div>}
                    size="small"
                    column={1}
                  >
                    <div className="table-responsive">
                      <Table
                        showSorterTooltip={false}
                        columns={[
                          { title: t("product"), dataIndex: "productName" },
                          { title: t("quantity"), dataIndex: "itemCount" },
                        ]}
                        dataSource={order.userShoppingCart.filter((item) => item.itemCount > 0)}
                        rowKey="productId"
                        pagination={false}
                      />
                    </div>
                  </Descriptions>
                )}
              </Drawer>
            )}
          </Card>
        </Col>
      </Row>
      <ButtonsWrapper style={{ justifyContent: "start" }}>
        <Button onClick={() => handlePeriodChange(30)} type={maxDaysAgo === 30 ? "primary" : "default"}>
          {t("last_30_days")}
        </Button>
        <Button onClick={() => handlePeriodChange(90)} type={maxDaysAgo === 90 ? "primary" : "default"}>
          {t("last_90_days")}
        </Button>
        <RangePicker onChange={handleCustomDateRange} placeholder={[t("start_date"), t("end_date")]} />
      </ButtonsWrapper>
      <Modal
        title={t("select_delivery_date")}
        open={dateSelectionModalVisible}
        onCancel={() => {
          setDateSelectionModalVisible(false);
          setSelectedDeliveryDate(null);
        }}
        footer={null}
      >
        <div style={{ textAlign: "center" }}>
          <p>{t("please_select_delivery_date")}</p>
          <DatePicker style={{ width: "100%" }} onChange={handleDateSelection} value={selectedDeliveryDate} />
        </div>
      </Modal>
      <AddShipmentModal
        visible={addShipmentModalVisible}
        onClose={closeAddModal}
        leads={leads}
        products={products}
        selectedDeliveryDate={selectedDeliveryDate}
        order={order}
        orders={orders}
      />
      <ConfirmDraftShipmentModal
        visible={confirmDraftModalVisible}
        onClose={closeConfirmDraftModal}
        order={selectedDraftOrder}
        products={products}
        leads={leads}
      />
      <EditCratesModal
        visible={editCratesModalVisible}
        onCancel={closeEditCratesModal}
        order={selectedOrderForCrates}
        orders={selectedOrdersForBulkCrates}
      />
      <BulkSendToFreshingBoxModal
        visible={bulkFreshingBoxModalVisible}
        onClose={closeBulkFreshingBoxModal}
        orders={selectedOrdersForBulkFreshingBox}
      />
    </>
  );
};

const FiltersWrapper = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 1rem;
  flex-wrap: wrap;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 1rem;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 576px) {
    align-items: flex-start;
  }
`;

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;

export default Shipments;
